import * as React from "react";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Checkbox from "@mui/material/Checkbox";
import CssBaseline from "@mui/material/CssBaseline";
import FormControlLabel from "@mui/material/FormControlLabel";
import Divider from "@mui/material/Divider";
import FormLabel from "@mui/material/FormLabel";
import FormControl from "@mui/material/FormControl";
import Link from "@mui/material/Link";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import Stack from "@mui/material/Stack";
import MuiCard from "@mui/material/Card";
import { styled, useTheme } from "@mui/material/styles";
import ColorModeSelect from "./theme/ColorModeselect";
import { Login } from "./api/login";
import { useAuth } from "./AuthContext/AuthContext";
import { useNavigate } from "react-router-dom";
import logo from "./theme/logonew.png";
import logo_Dark from "./theme/logonew_dark.png";
import { useState } from "react";

const Card = styled(MuiCard)(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  width: "100%",
  maxWidth: 450,
  padding: theme.spacing(2.5),
  gap: theme.spacing(2),
  [theme.breakpoints.up("sm")]: {
    padding: theme.spacing(4),
  },
  boxShadow:
    theme.palette.mode === "dark"
      ? `0 5px 15px 0 ${theme.palette.background.default}, 0 15px 35px -5px ${theme.palette.background.paper}`
      : `0 5px 15px 0 ${theme.palette.primary.main}20, 0 15px 35px -5px ${theme.palette.primary.dark}10`,
  backgroundColor: theme.palette.background.paper,
}));

const SignInContainer = styled(Stack)(({ theme }) => ({
  minHeight: "100dvh",
  width: "100%",
  padding: theme.spacing(2),
  position: "relative",
  alignItems: "center",
  gap: theme.spacing(1),
  [theme.breakpoints.up("sm")]: {
    padding: theme.spacing(4),
  },
  "&::before": {
    content: '""',
    display: "block",
    position: "absolute",
    zIndex: -1,
    inset: 0,
    backgroundImage:
      theme.palette.mode === "dark"
        ? `radial-gradient(ellipse at 50% 50%, ${theme.palette.primary.dark} 0%, ${theme.palette.background.default} 100%)`
        : `radial-gradient(ellipse at 50% 50%, ${theme.palette.primary.light} 0%, ${theme.palette.background.default} 100%)`,
    backgroundRepeat: "no-repeat",
  },
}));

export default function SignIn(props: { disableCustomTheme?: boolean }) {
  const theme = useTheme();
  const [emailError, setEmailError] = useState(false);
  const [emailErrorMessage, setEmailErrorMessage] = useState("");
  const [passwordError, setPasswordError] = useState(false);
  const [passwordErrorMessage, setPasswordErrorMessage] = useState("");
  const { login } = useAuth();
  const navigate = useNavigate();

  const validateInputs = async () => {
    const email = document.getElementById("email") as HTMLInputElement;
    const password = document.getElementById("password") as HTMLInputElement;

    let isValid = true;

    if (!email.value || !/\S+@\S+\.\S+/.test(email.value)) {
      setEmailError(true);
      setEmailErrorMessage("Please enter a valid email address.");
      isValid = false;
    } else {
      setEmailError(false);
      setEmailErrorMessage("");
    }

    if (!password.value || password.value.length < 6) {
      setPasswordError(true);
      setPasswordErrorMessage("Password must be at least 6 characters long.");
      isValid = false;
    } else {
      setPasswordError(false);
      setPasswordErrorMessage("");
    }

    if (isValid) {
      const loginData: Response = await Login(email.value, password.value);
      if (!loginData.ok) {
        const errorData = await loginData.json();
        setPasswordError(true);
        setPasswordErrorMessage(errorData.message);
        alert(errorData.message);
      } else {
        const getUserData = await loginData.json();
        login(
          getUserData.token,
          getUserData.role as string,
          getUserData.name,
          getUserData.id
        );
        navigate("/OperationalDashboard");
      }
    }
    return false;
  };

  return (
    <Box sx={{ minHeight: "100dvh", width: "100%" }}>
      <SignInContainer direction="column" justifyContent="center">
        <Box sx={{ width: "100%", maxWidth: 450, display: "flex", justifyContent: "flex-end" }}>
          <ColorModeSelect />
        </Box>
        <Card variant="outlined">
          <Typography
            component="h1"
            variant="h4"
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              width: "100%",
              fontSize: "clamp(1.75rem, 7vw, 2.15rem)",
              gap: 1,
              color:
                theme.palette.mode === "dark"
                  ? theme.palette.primary.light
                  : theme.palette.primary.dark,
            }}
          >
            Accedi
            <img
              src={theme.palette.mode === "dark" ? logo_Dark : logo}
              alt="Logo STT"
              style={{ width: "clamp(100px, 38vw, 150px)", maxWidth: "48%", height: "auto" }}
            />
          </Typography>
          <Box
            component="form"
            noValidate
            sx={{
              display: "flex",
              flexDirection: "column",
              width: "100%",
              gap: 2,
            }}
          >
            <FormControl>
              <FormLabel
                htmlFor="email"
                sx={{
                  color: theme.palette.primary.main,
                }}
              >
                Email
              </FormLabel>
              <TextField
                error={emailError}
                helperText={emailErrorMessage}
                id="email"
                type="email"
                name="email"
                placeholder="your@email.com"
                autoComplete="email"
                required
                fullWidth
                variant="outlined"
                color={emailError ? "error" : "primary"}
                sx={{
                  backgroundColor:
                    theme.palette.mode === "dark" ? "#232323" : "#FFFFFF",
                  input: {
                    color:
                      theme.palette.mode === "dark"
                        ? theme.palette.text.primary
                        : theme.palette.text.primary,
                  },
                }}
              />
            </FormControl>
            <FormControl>
              <FormLabel
                htmlFor="password"
                sx={{
                  color: theme.palette.primary.main,
                }}
              >
                Password
              </FormLabel>
              <TextField
                error={passwordError}
                helperText={passwordErrorMessage}
                name="password"
                placeholder="••••••"
                type="password"
                id="password"
                autoComplete="current-password"
                required
                fullWidth
                variant="outlined"
                color={passwordError ? "error" : "primary"}
                sx={{
                  backgroundColor:
                    theme.palette.mode === "dark" ? "#232323" : "#FFFFFF",
                  input: {
                    color:
                      theme.palette.mode === "dark"
                        ? theme.palette.text.primary
                        : theme.palette.text.primary,
                  },
                }}
              />
            </FormControl>
            <Button
              fullWidth
              variant="contained"
              onClick={validateInputs}
              sx={{
                backgroundColor: theme.palette.primary.main,
                color: theme.palette.primary.contrastText,
                "&:hover": {
                  backgroundColor: theme.palette.primary.dark,
                  color: "#fff",
                },
              }}
            >
              Log in
            </Button>
          </Box>
        </Card>
      </SignInContainer>
    </Box>
  );
}
